/*
 * Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { useCallback, useMemo, useState } from "react";
import { getAcceptableFilename } from "./upload-files-utils.js";
import { getAcceptableFiles } from "./upload-files-utils.js";
import { processFiles } from "./upload-files-utils.js";
import { UploadingFile } from "./uploading-file.jsx";
import { isArrayNotEmpty, isTrue } from "utils/empty-exists.js";
import { calculateBytesByUnit } from "./format.js";
import { ErrorCode, useDropzone } from "react-dropzone";
import { Logger } from "cms/utils/log/logger.js";
import { useAuth } from "cms/auth/hooks/use-auth";
import { useGetCurrentWebsite } from "hooks/use-current-website";
// import { useUpdateEffect } from "react-use";
// import { UploadedFile } from "./uploaded-file.jsx";

import { ReactComponent as UploadFileIcon } from "assets/images/icons/upload.svg";

// import UploadFileIcon from "assets/images/icons/upload.svg";
// PDF Document (pdf)
// JPEG Photo (jpg)
// JPEG Photo (jpeg)
// Portable Network Graphics (png)
// Word Document (doc)
// Word Document (docx)
// Excel Spreadsheet (xls)
// Excel Spreadsheet (xlsx)

const CONFIG = {
    maxFileSize: 30,
    maxFiles: 20,
    maxFileSizeBytes: calculateBytesByUnit(30, "mb"),
    files: { defaultMaxSize: calculateBytesByUnit(30, "mb") }, // 30mb
    fileUnit: "mb",
    allowOnlyFileTypes: ["png", "jpg", "jpeg", "pdf", "png", "doc", "docx", "xls", "xlsx"],
    isAllowOnlyFileTypes: true,
    errors: {
        tooLarge: (maxFileSize, fileUnit) => {
            return `File is too large, please ensure it is less than ${maxFileSize} ${fileUnit}.`;
        },
        tooMany: (maxFiles) => {
            return `The maximum number of files to upload is ${maxFiles}.`;
        },
        invalidType: (fileTypes) => {
            return `A file was not suitable. Allowed file types include: ${fileTypes}`;
        },
    },
};

const FileUpload = (props) => {
    
    const reference = props?.reference;
    const onUpload = props?.onUpload;
    const { currentWebsite: website } = useGetCurrentWebsite();
    const { token } = useAuth();

    const organization = useMemo(() => {
        return website?.organization;
    }, [website?.organization]);

    // if (!organization) {
    //     throw new Error("organization guid required");
    // }
    
    const defaultMaxSize = CONFIG.files.defaultMaxSize;

    /** @type {File[]} */
    const defaultFiles = [];
    const [files, setFiles] = useState(defaultFiles);
    
    const [completedFiles, setCompletedFiles] = useState([]);

    // useUpdateEffect(() => {
    //     const data = [];
    //     completedFiles.map((file) => {
    //         data.push({
    //             guid: file?.guid, 
    //             url: file?.url, 
    //             filename: file?.file_name, 
    //             title: file?.title
    //         });
    //     });
    //     onUpload(data);
    // }, [completedFiles]);

    const onDrop = useCallback(
        async (acceptedFiles) => {
            Logger.debug("Files Dropped.", { acceptedFiles });
            const renamedFiles = [];
            for (const file of acceptedFiles) {
                const replacementFile = new File([file], getAcceptableFilename(file), {
                    type: file.type,
                    lastModified: file.lastModified,
                });
                renamedFiles.push(replacementFile);
            }
            setFiles(renamedFiles);

            Logger.debug("upload [processFiles] started...");
            
            let uploaded_files = [];
            try {
                uploaded_files = await processFiles(token, renamedFiles, reference, organization);
            } catch (error) {
                Logger.error(error);
            }
            setCompletedFiles((_files) => {
                return [..._files, ...uploaded_files];
            });

            const data = [];
            uploaded_files.map((file) => {
                data.push({
                    guid: file?.guid,
                    url: file?.url,
                    filename: file?.file_name,
                    title: file?.title
                });
            });
            onUpload(data);

            setFiles([]);
            setCompletedFiles([]);
            
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [reference, organization]
    );

    const acceptableFiles = getAcceptableFiles(CONFIG.isAllowOnlyFileTypes, CONFIG.allowOnlyFileTypes);
    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragReject,
        // fileRejections,
    } = useDropzone({
        onDrop,
        validator: () => {
            // perform a check here to trigger an immediate file rejection when
            // attempting to upload additional files
            if (setCompletedFiles.length >= Number(CONFIG.maxFiles).valueOf()) {
                return {
                    code: ErrorCode.TooManyFiles,
                    // the message can be empty since we handle it in useFileUploadErrors
                    message: CONFIG.errors.tooMany(CONFIG.maxFiles)
                };
            }
            return null;
        },
        maxFiles: Number(CONFIG.maxFiles).valueOf(),
        maxSize: calculateBytesByUnit(CONFIG.maxFileSize, CONFIG.fileUnit) 
            ?? CONFIG.files.defaultMaxSize,
        multiple: Number(CONFIG.maxFiles).valueOf() > 1,
        uploadMultiple: Number(CONFIG.maxFiles).valueOf() > 1,
        // default -> does not limit types
        accept: acceptableFiles
    });

    // const { error, hasError } = useFileUploadErrors(question, fileRejections);

    const dragActiveStyle = isDragActive ? "drag-active" : "";
    const dragRejectStyle = isDragReject ? "drag-active" : "";

    /**
     * @param {File} file
     */
    const onCancelFile = (file) => {
        const fileName = file?.name;
        const filterByName = (_files) => {
            return _files?.filter((_file) => {
                return _file?.name !== fileName;
            });
        };
        setFiles(filterByName(files));
        // TODO: use API to delete files already uploaded?
    };
    
    // const onRemove = (attachment) => {
    //     const _guid = attachment?.guid;
    //     const filterByGuid = (_attachments) => {
    //         return _attachments?.filter((_attachment) => {
    //             return _attachment?.guid !== _guid;
    //         });
    //     };
    //     setCompletedFiles(filterByGuid(completedFiles));
    //     // TODO: use API to delete files already uploaded?
    // };

    const fileTypeList = useMemo(() => {
        let list = "";
        if (isArrayNotEmpty(CONFIG.allowOnlyFileTypes)) {
            list = CONFIG.allowOnlyFileTypes.map((type) => {
                let formattedType = type;
                // remove leading period (.) if it exists
                if (type[0] === ".") {
                    formattedType = type.slice(1);
                }
                return formattedType;
            })
                .sort()
                .join(", ");
        }
        return list;
    }, [CONFIG.allowOnlyFileTypes]);

    return (
        <div className="simplicity-forms-field-group dragActive">
            <div
                {...getRootProps()}
                className={`simplicity-forms-file-upload ${dragActiveStyle} ${dragRejectStyle}`}
            >
                <div className="simplicity-forms-file-upload-settings">
                    <p>Max file size: <span>{CONFIG.maxFileSize}{CONFIG.fileUnit?.toUpperCase()}</span></p>
                    <p>Max # of files: <span>{CONFIG.maxFiles}</span></p>
                    {CONFIG.isAllowOnlyFileTypes && fileTypeList && (
                        <p>Accepted file types: <span>{fileTypeList}</span></p>
                    )}
                </div>
                <div className="simplicity-forms-file-upload-content">
                    <div className="simplicity-forms-file-upload-icon">
                        <UploadFileIcon alt="upload-file-icon" />
                    </div>
                    <div className="simplicity-forms-file-upload-text">
                        <p>Drag and drop file(s) here or&nbsp;
                            <span className="simplicity-forms-file-upload-button">
                                <button type="button" className="form-btn-file-upload">
                                    browse files
                                </button>
                            </span>
                        </p>
                    </div>
                </div>
                {isTrue(isDragActive) && isTrue(isDragReject) && (
                    <p className="drag-problem">Problem detected. Please check acceptable file rules.</p>
                )}
                <input {...getInputProps()} />
            </div>

            <div className="files-container">
                {/*{isArrayNotEmpty(completedFiles) && completedFiles.map((attachment) => (*/}
                {/*    <UploadedFile*/}
                {/*        key={attachment?.guid}*/}
                {/*        attachment={attachment}*/}
                {/*        onRemove={onRemove}*/}
                {/*    />*/}
                {/*))}*/}

                {isArrayNotEmpty(files) && files.map((file) => (
                    <UploadingFile
                        key={file?.name}
                        file={file}
                        onCancelFile={onCancelFile}
                    />
                ))}
            </div>
            {/*<ErrorMsg keyid={guid} hasError={hasError} type="error" error={error} />*/}
        </div>
    );
};

export { FileUpload };

